import * as THREE from 'three';

class MotorRenderer {
  constructor(container) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.container.appendChild(this.renderer.domElement);
    
    // Setup lighting
    this.setupLights();
    
    // Create motor components
    this.createMotorComponents();
    
    // Setup camera position
    this.camera.position.z = 5;
    
    // Energy flow arrows
    this.arrows = {
      rotorToConverter: null,
      converterToGrid: null,
      converterToDCMotor: null
    };
    
    this.createEnergyFlowArrows();
    
    // Animation loop
    this.animate();
  }
  
  setupLights() {
    const ambientLight = new THREE.AmbientLight(0x404040);
    this.scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(1, 1, 1);
    this.scene.add(directionalLight);
  }
  
  createMotorComponents() {
    // Create stator
    const statorGeometry = new THREE.CylinderGeometry(2, 2, 3, 32);
    const statorMaterial = new THREE.MeshPhongMaterial({ color: 0x8888ff, transparent: true, opacity: 0.7 });
    this.stator = new THREE.Mesh(statorGeometry, statorMaterial);
    this.scene.add(this.stator);
    
    // Create rotor
    const rotorGeometry = new THREE.CylinderGeometry(1.5, 1.5, 3.5, 32);
    const rotorMaterial = new THREE.MeshPhongMaterial({ color: 0xff8888 });
    this.rotor = new THREE.Mesh(rotorGeometry, rotorMaterial);
    this.scene.add(this.rotor);
    
    // Create slip rings
    const slipRingGeometry = new THREE.TorusGeometry(0.3, 0.1, 16, 100);
    const slipRingMaterial = new THREE.MeshPhongMaterial({ color: 0xffff00 });
    
    this.slipRing1 = new THREE.Mesh(slipRingGeometry, slipRingMaterial);
    this.slipRing1.position.y = 2;
    this.slipRing1.rotation.x = Math.PI / 2;
    this.scene.add(this.slipRing1);
    
    this.slipRing2 = new THREE.Mesh(slipRingGeometry, slipRingMaterial);
    this.slipRing2.position.y = 1.5;
    this.slipRing2.rotation.x = Math.PI / 2;
    this.scene.add(this.slipRing2);
    
    // Create converter box
    const converterGeometry = new THREE.BoxGeometry(1.5, 1, 1);
    const converterMaterial = new THREE.MeshPhongMaterial({ color: 0x00ff00 });
    this.converter = new THREE.Mesh(converterGeometry, converterMaterial);
    this.converter.position.set(3, 0, 0);
    this.scene.add(this.converter);
    
    // Create DC motor (for Kramer drive)
    const dcMotorGeometry = new THREE.CylinderGeometry(0.8, 0.8, 1.5, 32);
    const dcMotorMaterial = new THREE.MeshPhongMaterial({ color: 0x00ffff });
    this.dcMotor = new THREE.Mesh(dcMotorGeometry, dcMotorMaterial);
    this.dcMotor.position.set(5, 0, 0);
    this.dcMotor.rotation.z = Math.PI / 2;
    this.scene.add(this.dcMotor);
  }
  
  createEnergyFlowArrows() {
    // Create arrow from rotor to converter
    const arrowDir1 = new THREE.Vector3(1, 0, 0).normalize();
    const rotorToConverterArrow = new THREE.ArrowHelper(
      arrowDir1,
      new THREE.Vector3(1.8, 0, 0),
      1,
      0xff0000,
      0.2,
      0.1
    );
    this.scene.add(rotorToConverterArrow);
    this.arrows.rotorToConverter = rotorToConverterArrow;
    
    // Create arrow from converter to grid (Scherbius)
    const arrowDir2 = new THREE.Vector3(1, 1, 0).normalize();
    const converterToGridArrow = new THREE.ArrowHelper(
      arrowDir2,
      new THREE.Vector3(3.5, 0, 0),
      1,
      0x0000ff,
      0.2,
      0.1
    );
    this.scene.add(converterToGridArrow);
    this.arrows.converterToGrid = converterToGridArrow;
    
    // Create arrow from converter to DC motor (Kramer)
    const arrowDir3 = new THREE.Vector3(1, 0, 0).normalize();
    const converterToDCMotorArrow = new THREE.ArrowHelper(
      arrowDir3,
      new THREE.Vector3(3.8, 0, 0),
      1,
      0x00ff00,
      0.2,
      0.1
    );
    this.scene.add(converterToDCMotorArrow);
    this.arrows.converterToDCMotor = converterToDCMotorArrow;
  }
  
  updateEnergyFlow(model) {
    // Update arrow sizes based on power flow
    const rotorPowerScale = model.rotorPower / model.ratedPower;
    const recoveredPowerScale = model.recoveredPower / model.ratedPower;
    
    // Scale arrows based on power flow
    this.arrows.rotorToConverter.scale.set(1, rotorPowerScale * 3, rotorPowerScale * 3);
    
    if (model.driveType === 'scherbius') {
      this.arrows.converterToGrid.visible = true;
      this.arrows.converterToDCMotor.visible = false;
      this.arrows.converterToGrid.scale.set(1, recoveredPowerScale * 3, recoveredPowerScale * 3);
    } else { // Kramer
      this.arrows.converterToGrid.visible = false;
      this.arrows.converterToDCMotor.visible = true;
      this.arrows.converterToDCMotor.scale.set(1, recoveredPowerScale * 3, recoveredPowerScale * 3);
    }
  }
  
  updateRotorSpeed(speed) {
    // Animate rotor rotation based on speed
    this.rotor.rotation.y += speed * 0.01;
  }
  
  // Add this new method
  updateMotor(data, driveType) {
    // Update the motor visualization based on simulation data
    this.updateRotorSpeed(data.rotorSpeed);
    
    // Update the energy flow visualization
    this.updateEnergyFlow({
      rotorPower: data.rotorPower,
      recoveredPower: data.recoveredPower,
      ratedPower: data.ratedPower || 50000, // Default if not provided
      driveType: driveType
    });
    
    // Update visibility based on drive type
    if (driveType === 'scherbius') {
      if (this.dcMotor) this.dcMotor.visible = false;
    } else { // Kramer
      if (this.dcMotor) this.dcMotor.visible = true;
    }
  }
  
  animate() {
    requestAnimationFrame(this.animate.bind(this));
    this.renderer.render(this.scene, this.camera);
  }
  
  resize() {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
  }

  // Add AR support
  enableARMode() {
    // Setup AR scene
    if (navigator.xr) {
      // Check if AR is supported
      navigator.xr.isSessionSupported('immersive-ar')
        .then(supported => {
          if (supported) {
            // XR API implementation for AR
            this.renderer.xr.enabled = true;
            
            // Create AR button
            const arButton = document.createElement('button');
            arButton.textContent = 'Enter AR';
            arButton.onclick = () => {
              navigator.xr.requestSession('immersive-ar')
                .then(session => {
                  this.renderer.xr.setSession(session);
                })
                .catch(error => {
                  console.warn('Failed to start AR session:', error);
                });
            };
            
            this.container.appendChild(arButton);
          } else {
            console.log('Immersive AR not supported on this device');
            // Optionally display a message to the user
          }
        })
        .catch(error => {
          console.warn('Error checking AR support:', error);
        });
    } else {
      console.log('WebXR not supported in this browser');
      // Optionally display a message to the user
    }
  }
  
  // Add comparison mode visualization
  enableComparisonMode() {
    // Create split view for Scherbius and Kramer
    this.camera.position.x = -2;
    
    // Clone motor components for comparison
    const kramerGroup = new THREE.Group();
    // Clone and add components to kramerGroup
    kramerGroup.position.x = 10;
    this.scene.add(kramerGroup);
    
    // Add labels
    // Add performance comparison indicators
  }
  
  // Add holographic effects
  addHolographicEffects() {
    // Add glow effect
    const glowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0x00ffff) },
        // Add shader uniforms
      },
      vertexShader: /* glsl */`
        // Vertex shader code
      `,
      fragmentShader: /* glsl */`
        // Fragment shader code
      `,
      transparent: true,
      blending: THREE.AdditiveBlending
    });
    
    // Apply to components
    const glowStator = new THREE.Mesh(this.stator.geometry.clone(), glowMaterial);
    glowStator.scale.multiplyScalar(1.05);
    this.stator.add(glowStator);
  }
}

export default MotorRenderer;